import { useState } from "react";
import { BusFront, Clock, CheckCircle2, Loader2, Send, ArrowLeftRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const PRESETS = [5, 10, 15, 20, 30, 45];
const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

export function busStatusPreview(bus, f) {
  const parts = [];
  const sub = f.sub_number.trim();
  const shown = sub || bus.number;
  if (sub && sub !== bus.number) {
    parts.push(f.permanent ? `Bus ${bus.number} is now Bus ${sub} going forward.` : `Bus ${bus.number} is replaced by Bus ${sub} today — please look for Bus ${sub}.`);
  }
  if (f.status === "delayed") parts.push(`Bus ${shown} is running about ${f.delay_minutes || "a few"} minutes late today.`);
  else parts.push(parts.length ? `Bus ${shown} is on time.` : `Bus ${shown} is back on time.`);
  if (f.note.trim()) parts.push(f.note.trim());
  return parts.join(" ");
}

export default function BusStatusDialog({ bus, onClose, onSaved }) {
  const [f, setF] = useState({
    status: bus.status || "on_time", delay_minutes: bus.delay_minutes || 15, note: bus.status_note || "",
    sub_number: bus.sub_number || "", permanent: false, notify: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/buses/${bus.id}/status`, { ...f, delay_minutes: Number(f.delay_minutes) || 0 });
      toast.success(f.notify ? `Status saved — parents of ${bus.rider_count ?? ""} riders are being notified` : "Status saved");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bus-status-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BusFront className="h-5 w-5 text-amber-600" /> Bus {bus.number} · today's status
          </DialogTitle>
          <DialogDescription>Status resets to "On time" automatically at the end of the day.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-2" data-testid="bus-status-choice">
            {[["on_time", "On time", CheckCircle2, "border-emerald-500 bg-emerald-50 text-emerald-800"], ["delayed", "Delayed", Clock, "border-amber-500 bg-amber-50 text-amber-800"]].map(([v, label, Icon, active]) => (
              <button type="button" key={v} onClick={() => setF((s) => ({ ...s, status: v, note: v === "on_time" ? "" : s.note }))} data-testid={`bus-status-${v}`}
                className={`h-12 rounded-xl border-2 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
                  f.status === v ? active : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>

          {f.status === "delayed" && (
            <div data-testid="bus-delay-section">
              <span className="block mb-1.5 text-[11px] font-medium uppercase tracking-widest text-slate-500">How late?</span>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((m) => (
                  <button type="button" key={m} onClick={() => set("delay_minutes", m)} data-testid={`bus-delay-${m}`}
                    className={`h-9 rounded-lg px-3 text-sm font-semibold transition ${Number(f.delay_minutes) === m ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    {m} min
                  </button>
                ))}
                <input type="number" min={1} max={600} value={f.delay_minutes} onChange={(e) => set("delay_minutes", e.target.value)}
                  data-testid="bus-delay-custom" className="h-9 w-24 rounded-lg border border-slate-200 px-2 text-sm text-center outline-none focus:border-primary" />
              </div>
            </div>
          )}

          <label className="block">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><ArrowLeftRight className="h-3 w-3" /> Substitute bus number (optional)</span>
            <input value={f.sub_number} onChange={(e) => set("sub_number", e.target.value)} placeholder={`Leave empty to keep Bus ${bus.number}`}
              data-testid="bus-sub-number" className={inputCls} maxLength={20} />
            {f.sub_number.trim() && f.sub_number.trim() !== bus.number && (
              <label className="mt-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={f.permanent} onChange={(e) => set("permanent", e.target.checked)} data-testid="bus-sub-permanent" className="h-4 w-4 rounded border-slate-300 text-primary" />
                Change the route's number permanently (otherwise today only)
              </label>
            )}
          </label>

          <label className="block">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Note to parents (optional)</span>
            <textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} maxLength={300} data-testid="bus-status-note"
              placeholder="e.g. Heavy traffic on Main St." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </label>

          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-700" data-testid="bus-status-preview">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Parents will read</div>
            {busStatusPreview(bus, f)}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.notify} onChange={(e) => set("notify", e.target.checked)} data-testid="bus-status-notify" className="h-4 w-4 rounded border-slate-300 text-primary" />
            Notify parents now (in-app message, push, email/SMS per Parent Alerts settings)
          </label>

          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="bus-status-save"
              className="h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-60 transition">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {f.notify ? "Save & notify parents" : "Save status"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BusStatusChip({ bus, testid }) {
  const sub = bus.sub_number && bus.sub_number !== bus.number;
  if (bus.status !== "delayed" && !sub) {
    return <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> On time</span>;
  }
  return (
    <span data-testid={testid} className="inline-flex flex-wrap items-center gap-1.5">
      {bus.status === "delayed" && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800"><Clock className="h-3 w-3" /> Delayed {bus.delay_minutes ? `${bus.delay_minutes} min` : ""}</span>
      )}
      {sub && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-bold text-violet-800"><ArrowLeftRight className="h-3 w-3" /> Running as Bus {bus.sub_number}</span>}
    </span>
  );
}
